import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaBars, FaTimes, FaSignOutAlt, FaUser, FaMapMarkerAlt } from "react-icons/fa";
import "./Header.css";
import Logo from "./Logo";
import { logout } from "./AuthUtils";
import { useLocationPermission, PERMISSION_STATUS } from "../context/LocationPermissionContext";

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userLocation, setUserLocation] = useState("Loading...");

  const { permissionStatus, location: coords, requestLocation, showLocationSettings } = useLocationPermission();

  const toggleMobileMenu = () => {
    setMobileOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setMobileOpen(false);
  };

  const handleLogout = async () => {
    try {
      logout();
      localStorage.clear();
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }
      navigate("/phone", { replace: true });
      window.location.reload(true);
      closeMobileMenu();
    } catch (error) {
      console.error("Error during logout:", error);
      navigate("/phone", { replace: true });
      closeMobileMenu();
    }
  };

  // Fetch city name from coordinates
  useEffect(() => {
    const fetchCityName = async (latitude, longitude) => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`,
          {
            headers: {
              "User-Agent": "VaahanBuddy/1.0 (contact: hello@vaahanbuddy.com)",
            },
          }
        );
        const data = await response.json();
        const city =
          data.address.city ||
          data.address.town ||
          data.address.village ||
          data.address.state ||
          "Unknown Location";
        setUserLocation(city);
      } catch (error) {
        console.error("Error fetching location:", error);
        setUserLocation("Unknown Location");
      }
    };

    if (coords) {
      fetchCityName(coords.latitude, coords.longitude);
    } else if (permissionStatus === PERMISSION_STATUS.DENIED) {
      setUserLocation("Enable Location");
    } else if (permissionStatus === PERMISSION_STATUS.UNAVAILABLE) {
      setUserLocation("Location unavailable");
    } else {
      // Do not request location automatically on mount
      // Wait for user interaction or specific feature usage
      setUserLocation("Set Location");
    }
  }, [coords, permissionStatus, requestLocation]);

  const handleLocationClick = () => {
    if (permissionStatus === PERMISSION_STATUS.DENIED || !coords) {
      showLocationSettings();
    }
  };

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobileOpen]);

  const isLocationClickable = permissionStatus === PERMISSION_STATUS.DENIED || !coords;

  return (
    <header className="header-box">
      <div className="header-section">
        {/* Logo + App Name */}
        <div className="header-left" onClick={closeMobileMenu}>
          <Logo />
        </div>

        {/* Mobile menu toggle */}
        <button
          className="menu-toggle"
          aria-label="Toggle navigation"
          onClick={toggleMobileMenu}
        >
          {mobileOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
        </button>

        {/* Overlay for clicking outside to close */}
        {mobileOpen && (
          <div className="menu-overlay" onClick={closeMobileMenu}></div>
        )}

        {/* Navigation */}
        <nav className={`main-nav ${mobileOpen ? "open" : ""}`}>
          {mobileOpen && (
            <div className="mobile-menu-header">
              <div onClick={closeMobileMenu} className="logo-link">
                <Logo />
                <h1 className="app-title">VaahanBuddy</h1>
              </div>
              <button className="close-menu-btn" onClick={closeMobileMenu}>
                <FaTimes />
              </button>
            </div>
          )}

          {/* Mobile Location Display */}
          {mobileOpen && (
            <div
              className={`mobile-location ${isLocationClickable ? 'clickable' : ''}`}
              onClick={isLocationClickable ? handleLocationClick : undefined}
            >
              <FaMapMarkerAlt />
              <span>{userLocation}</span>
            </div>
          )}

          <ul>
            {[
              { to: "/searchDetails", label: "Vaahan Search" },
              { to: "/carInsurance", label: "Insurance" },
              { to: "/loan", label: "Loans" },
              { to: "/garage", label: "Garage" },
              { to: "/contact-form", label: "Contact" },
            ].map(({ to, label }) => (
              <li
                key={to}
                className={location.pathname === to ? "active" : ""}
                onClick={closeMobileMenu}
              >
                <Link to={to}>{label}</Link>
              </li>
            ))}

            {/* Mobile Profile Link */}
            <li
              className={`mobile-only-menu-item profile-item ${location.pathname === "/profile" ? "active" : ""
                }`}
              onClick={closeMobileMenu}
            >
              <Link to="/profile" className="profile-link">
                <FaUser /> Profile
              </Link>
            </li>

            {/* Mobile Logout */}
            <li
              className="mobile-only-menu-item logout-item"
              onClick={handleLogout}
            >
              <span className="logout-link">
                <FaSignOutAlt /> Logout
              </span>
            </li>
          </ul>
        </nav>

        {/* Right side content (desktop only) */}
        <div className="header-right">
          <div
            className={`location-selector ${isLocationClickable ? 'clickable' : ''}`}
            onClick={isLocationClickable ? handleLocationClick : undefined}
          >
            <span>{userLocation}</span>
          </div>
          <div className="user-actions">
            <Link
              to="/profile"
              className="login-btn"
              onClick={closeMobileMenu}
            >
              {location.pathname === "/profile" ? "Profile" : "Profile"}
            </Link>
            <button className="logout-btn" onClick={handleLogout}>
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
